/**
 * oddit-header — Interactive behavior for header navigation
 * Handles: mobile drawer, desktop mega menu, search drawer, cart drawer,
 *          mobile submenus, escape key, cart count badge
 */

(function () {
  'use strict';

  const header = document.querySelector('.oddit-header');
  if (!header) return;

  // ==========================================================================
  // Mobile Drawer
  // ==========================================================================

  const menuToggle = header.querySelector('.oddit-header__menu-toggle');
  const drawer = header.querySelector('[data-drawer]');
  const drawerClose = header.querySelector('.oddit-header__drawer-close');
  const drawerOverlay = header.querySelector('[data-drawer-close]');

  // Expose the announcement bar + header stack height so sections that sit
  // under/behind it (e.g. the hero banner) can offset their content.
  const headerGroup = document.querySelector('.header-group');

  function setHeaderGroupHeight() {
    if (!headerGroup) return;
    document.documentElement.style.setProperty('--oddit-header-group-height', headerGroup.offsetHeight + 'px');
  }

  setHeaderGroupHeight();
  window.addEventListener('resize', setHeaderGroupHeight);

  function positionDrawer() {
    if (!drawer) return;
    var rect = header.getBoundingClientRect();
    var top = rect.bottom;
    var height = window.innerHeight - top;
    header.style.setProperty('--oddit-drawer-top', top + 'px');
    header.style.setProperty('--oddit-drawer-height', height + 'px');
  }

  function openDrawer() {
    if (!drawer) return;
    positionDrawer();
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (menuToggle) {
      menuToggle.classList.add('is-open');
      menuToggle.setAttribute('aria-label', 'Close menu');
    }
    applyScrollColors();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    resetSubmenus();
    if (menuToggle) {
      menuToggle.classList.remove('is-open');
      menuToggle.setAttribute('aria-label', 'Open menu');
      menuToggle.focus();
    }
    updateHeaderTransparency();
  }

  window.addEventListener('resize', function () {
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      positionDrawer();
    }
  });

  if (menuToggle) menuToggle.addEventListener('click', function () {
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      closeDrawer();
    } else {
      openDrawer();
    }
  });
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerOverlay) drawerOverlay.addEventListener('click', closeDrawer);

  // ==========================================================================
  // Desktop Mega Menu — one panel per mega_menu block, keyed by nav handle
  // ==========================================================================

  const navItems = header.querySelectorAll('.oddit-header__nav-item');
  const megamenuOverlay = header.querySelector('[data-megamenu-overlay]');
  let megamenuTimeout = null;
  let activePanel = null;

  function showMegamenuOverlay() {
    if (!megamenuOverlay) return;
    megamenuOverlay.classList.add('is-visible');
    // Position overlay below the header + mega menu after layout
    requestAnimationFrame(function () {
      var megamenu = header.querySelector('.oddit-header__megamenu[aria-hidden="false"]');
      var bottom = header.getBoundingClientRect().bottom;
      if (megamenu) {
        bottom = megamenu.getBoundingClientRect().bottom;
      }
      megamenuOverlay.style.top = bottom + 'px';
    });
  }

  function hideMegamenuOverlay() {
    if (megamenuOverlay) megamenuOverlay.classList.remove('is-visible');
  }

  function clearActiveNavLink() {
    header.querySelectorAll('.oddit-header__nav-link--megamenu-active').forEach(function (el) {
      el.classList.remove('oddit-header__nav-link--megamenu-active');
    });
  }

  function showMegaMenu(targetId) {
    clearTimeout(megamenuTimeout);

    applyScrollColors();

    // Hide any currently visible panel
    if (activePanel && activePanel !== targetId) {
      const prev = header.querySelector('[data-megamenu="' + activePanel + '"]');
      if (prev) prev.setAttribute('aria-hidden', 'true');
    }

    const panel = header.querySelector('[data-megamenu="' + targetId + '"]');
    if (!panel) return;

    panel.setAttribute('aria-hidden', 'false');
    activePanel = targetId;

    // Underline the active nav link
    clearActiveNavLink();
    var activeLink = header.querySelector('[data-megamenu-target="' + targetId + '"]');
    if (activeLink) activeLink.classList.add('oddit-header__nav-link--megamenu-active');

    showMegamenuOverlay();
  }

  function hideMegaMenu() {
    clearTimeout(megamenuTimeout);
    megamenuTimeout = setTimeout(function () {
      if (activePanel) {
        const panel = header.querySelector('[data-megamenu="' + activePanel + '"]');
        if (panel) panel.setAttribute('aria-hidden', 'true');
        activePanel = null;
      }
      clearActiveNavLink();
      hideMegamenuOverlay();
      updateHeaderTransparency();
    }, 200);
  }

  navItems.forEach(function (item) {
    const link = item.querySelector('.oddit-header__nav-link');
    if (link && link.hasAttribute('data-has-megamenu')) {
      const targetId = link.getAttribute('data-megamenu-target');
      item.addEventListener('mouseenter', function () {
        showMegaMenu(targetId);
      });
      // Don't hide on mouseleave from the nav item — let the header-level
      // mouseleave handle closing so the user can move to the mega menu freely
    } else {
      item.addEventListener('mouseenter', hideMegaMenu);
    }
  });

  // Keep mega menu open while hovering anywhere in the header (nav + mega menu panel)
  header.addEventListener('mouseleave', function () {
    if (activePanel) hideMegaMenu();
  });

  // Cancel any pending hide when re-entering the header
  header.addEventListener('mouseenter', function () {
    if (activePanel) clearTimeout(megamenuTimeout);
  });

  // Close mega menu when hovering or clicking the overlay
  if (megamenuOverlay) {
    function closeMegaMenuImmediate() {
      clearTimeout(megamenuTimeout);
      if (activePanel) {
        var panel = header.querySelector('[data-megamenu="' + activePanel + '"]');
        if (panel) panel.setAttribute('aria-hidden', 'true');
        activePanel = null;
      }
      clearActiveNavLink();
      hideMegamenuOverlay();
      updateHeaderTransparency();
    }
    megamenuOverlay.addEventListener('mouseenter', closeMegaMenuImmediate);
    megamenuOverlay.addEventListener('click', closeMegaMenuImmediate);
  }

  // ==========================================================================
  // Search Drawer Toggle (desktop + mobile)
  // ==========================================================================

  header.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (window.odditSearchDrawer) window.odditSearchDrawer.open();
    });
  });

  var desktopSearchInput = header.querySelector('.oddit-header__search-input');
  if (desktopSearchInput) {
    desktopSearchInput.addEventListener('focus', function () {
      if (window.odditSearchDrawer) window.odditSearchDrawer.open();
    });
  }

  // ==========================================================================
  // Cart Drawer Toggle (desktop + mobile)
  // ==========================================================================

  header.querySelectorAll('[data-cart-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (window.odditCartDrawer) window.odditCartDrawer.open();
    });
  });

  // ==========================================================================
  // Cart Count Badge — listens for cart:updated custom events
  // ==========================================================================

  document.addEventListener('cart:updated', function (e) {
    const cart = e.detail && e.detail.cart;
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? '' : 'none';
    });
  });

  // ==========================================================================
  // Mobile Drawer Submenus (second-level navigation)
  // ==========================================================================

  const drawerLevels = header.querySelector('[data-drawer-levels]');

  function openSubmenu(target) {
    if (!drawerLevels) return;
    drawerLevels.querySelectorAll('[data-submenu]').forEach(function (sm) {
      sm.classList.remove('is-active');
    });
    const targetSubmenu = drawerLevels.querySelector('[data-submenu="' + target + '"]');
    if (targetSubmenu) {
      targetSubmenu.classList.add('is-active');
      drawerLevels.classList.add('oddit-header__drawer-levels--submenu-open');
      const panel = header.querySelector('.oddit-header__drawer-panel');
      if (panel) panel.scrollTop = 0;
    }
  }

  function closeSubmenu() {
    if (!drawerLevels) return;
    drawerLevels.classList.remove('oddit-header__drawer-levels--submenu-open');
    setTimeout(function () {
      drawerLevels.querySelectorAll('[data-submenu]').forEach(function (sm) {
        sm.classList.remove('is-active');
      });
    }, 300);
  }

  function resetSubmenus() {
    if (!drawerLevels) return;
    drawerLevels.classList.remove('oddit-header__drawer-levels--submenu-open');
    drawerLevels.querySelectorAll('[data-submenu]').forEach(function (sm) {
      sm.classList.remove('is-active');
    });
  }

  header.querySelectorAll('.oddit-header__drawer-link[data-submenu-target]').forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      const target = trigger.getAttribute('data-submenu-target');
      if (target) openSubmenu(target);
    });
  });

  header.querySelectorAll('[data-submenu-back]').forEach(function (btn) {
    btn.addEventListener('click', closeSubmenu);
  });

  // ==========================================================================
  // Escape key — closes drawer and any open mega menu panel
  // ==========================================================================

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
      closeDrawer();
    }
    if (activePanel) {
      const panel = header.querySelector('[data-megamenu="' + activePanel + '"]');
      if (panel) panel.setAttribute('aria-hidden', 'true');
      activePanel = null;
      clearTimeout(megamenuTimeout);
      hideMegamenuOverlay();
    }
  });

  // ==========================================================================
  // Header color management — default & scrolled states + transparent (homepage)
  // ==========================================================================

  var scrollBg = header.getAttribute('data-scroll-bg') || '#ffffff';
  var scrollText = header.getAttribute('data-scroll-text') || '#000000';
  var transparentText = header.getAttribute('data-transparent-text') || '#ffffff';
  var transparentEnabled = header.getAttribute('data-transparent-enabled') === 'true';
  var isHomepage = document.body.classList.contains('template-index');

  function isLightColor(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
  }

  // The header only overlays the first section when that section is a
  // transparent hero (which is built to sit full-bleed behind the fixed nav).
  // For any other first section — a banner, a non-hero section, or no hero at
  // all — return null so the header falls back to its opaque, in-flow (sticky)
  // state and never overlaps the content (ELA-186).
  function getOverlayHeroStyle() {
    var main = document.getElementById('MainContent');
    var firstSection = main && main.querySelector('.shopify-section');
    var hero = firstSection && firstSection.querySelector('.oddit-section-hero[data-header-style]');
    if (!hero) return null;
    var style = hero.getAttribute('data-header-style');
    if (style === 'transparent_white' || style === 'transparent_black') return style;
    return null;
  }

  function applyHeaderStyle() {
    var overlay = transparentEnabled && getOverlayHeroStyle() !== null;
    document.body.classList.toggle('oddit-header-opaque', !overlay);
  }

  function applyDefaultColors() {
    header.style.removeProperty('--oddit-header-bg');
    header.style.removeProperty('--oddit-header-text');
    header.classList.remove('oddit-header--transparent');
    header.classList.remove('oddit-header--invert-logo');
  }

  function applyScrollColors() {
    header.style.setProperty('--oddit-header-bg', scrollBg);
    header.style.setProperty('--oddit-header-text', scrollText);
    header.classList.remove('oddit-header--transparent');
    header.classList.remove('oddit-header--invert-logo');
  }

  function applyTransparent() {
    header.style.setProperty('--oddit-header-text', transparentText);
    header.style.removeProperty('--oddit-header-bg');
    header.classList.add('oddit-header--transparent');
    if (isLightColor(transparentText)) {
      header.classList.add('oddit-header--invert-logo');
    } else {
      header.classList.remove('oddit-header--invert-logo');
    }
  }

  var updateHeaderTransparency = function () {
    if (drawer && drawer.getAttribute('aria-hidden') === 'false') return;

    if (window.scrollY > 10) {
      applyScrollColors();
    } else if (isHomepage && transparentEnabled && getOverlayHeroStyle() !== null) {
      applyTransparent();
    } else {
      applyDefaultColors();
    }
  };

  if (isHomepage) applyHeaderStyle();
  updateHeaderTransparency();
  window.addEventListener('scroll', updateHeaderTransparency, { passive: true });

  if (isHomepage) {
    // Re-evaluate on any section add / remove / reorder in the theme editor so
    // the overlay vs. opaque state always tracks whatever section is first.
    function reapplyHeaderState() {
      applyHeaderStyle();
      updateHeaderTransparency();
    }
    document.addEventListener('shopify:section:load', reapplyHeaderState);
    document.addEventListener('shopify:section:unload', reapplyHeaderState);
  }
})();
